/**
 * IPLoginHandler
 *
 * Invisible component registered as an appExtra. Fires once on mount.
 *
 * Two-stage auth detection:
 *
 * Stage 1 — IP login (JWT):
 *   Calls GET /++api++/@ip-login. If the backend recognises the IP and
 *   returns a JWT token, dispatches LOGIN_SUCCESS into Redux so Volto
 *   treats the user as fully logged in.
 *
 * Stage 2 — Content probe (cookie session fallback):
 *   If Stage 1 fails (e.g. nginx obscures the real IP), probes a known
 *   restricted content item using credentials: 'include'. If the request
 *   returns 200, the user has an active Plone session cookie (set by the
 *   backend IP check) even though Volto doesn't have a JWT. In this case
 *   we set the ipAuthLogin flag in localStorage and fire a custom event
 *   so AuthBanner can update its state without a Redux token.
 *
 * INTERIM NOTE: Stage 2 relies on a hardcoded restricted content path.
 * If that content is moved, renamed, or unpublished, Stage 2 will silently
 * fail and cookie-session users will appear anonymous in the banner.
 * Replace with a lightweight dedicated endpoint when possible.
 *
 * Nothing is rendered. This component is purely a side-effect carrier.
 */

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LOGIN } from '@plone/volto/constants/ActionTypes';
import { getUser } from '@plone/volto/actions';

// INTERIM: hardcoded restricted content path used as a session probe.
// Replace with a dedicated lightweight endpoint when nginx IP forwarding
// is resolved or a better solution is available.
const CONTENT_PROBE_PATH = '/++api++/products/risk-alert/running-on-fumes';

const IPLoginHandler = () => {
  const dispatch = useDispatch();
  const hasFired = useRef(false);

  const existingToken = useSelector((state) => state.userSession?.token);
  const existingUser = useSelector((state) => state.users?.user);

  useEffect(() => {

    // Already fully authenticated — nothing to do
    if (existingToken && existingUser) {
      console.log('[IPLoginHandler] Already authenticated, skipping IP check.');
      return;
    }

    // Token exists but user profile missing — fetch the profile and stop
    if (existingToken && !existingUser) {
      console.log('[IPLoginHandler] Token present, fetching user profile.');
      dispatch(getUser('@me'));
      return;
    }

    // Prevent duplicate calls on remount
    if (hasFired.current) {
      return;
    }
    hasFired.current = true;

    const checkIpLogin = async () => {

      // --- Stage 1: IP login (JWT) ---
      console.log('[IPLoginHandler] Stage 1: Checking IP login...');

      let ipResponse;
      try {
        ipResponse = await fetch('/++api++/@ip-login', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'include',
        });
      } catch (networkError) {
        console.warn('[IPLoginHandler] Stage 1: Network error:', networkError);
        hasFired.current = false;
        return;
      }

      if (ipResponse.ok) {
        let data;
        try {
          data = await ipResponse.json();
        } catch (e) {
          console.warn('[IPLoginHandler] Stage 1: JSON parse error:', e);
        }

        if (data?.token) {
          console.log('[IPLoginHandler] Stage 1: IP login successful — dispatching token.');

          dispatch({
            type: `${LOGIN}_SUCCESS`,
            result: { token: data.token },
          });

          localStorage.setItem('auth', data.token);
          localStorage.setItem('ipAuthLogin', 'true');
          dispatch(getUser('@me'));
          return;
        }

        console.warn('[IPLoginHandler] Stage 1: Response OK but no token — falling through to Stage 2.');
      } else {
        console.log(`[IPLoginHandler] Stage 1: Returned ${ipResponse.status} — falling through to Stage 2.`);
      }

      // --- Stage 2: Content probe (cookie session fallback) ---
      // @ip-login didn't return a token — likely nginx obscuring the real IP.
      // Probe a known restricted item instead. A 200 means an active Plone
      // session cookie is present, even though Volto has no JWT.
      console.log('[IPLoginHandler] Stage 2: Probing for active session...');

      let probeResponse;
      try {
        probeResponse = await fetch(CONTENT_PROBE_PATH, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'include',
        });
      } catch (networkError) {
        console.warn('[IPLoginHandler] Stage 2: Network error:', networkError);
        return;
      }

      if (probeResponse.ok) {
        console.log('[IPLoginHandler] Stage 2: Active session detected — setting IP auth flag.');
        localStorage.setItem('ipAuthLogin', 'true');
        // No JWT available so we can't update Redux.
        // Fire a custom event so AuthBanner can update its local state.
        window.dispatchEvent(new Event('ipAuthDetected'));
      } else {
        console.log(`[IPLoginHandler] Stage 2: Probe returned ${probeResponse.status} — user is anonymous.`);
      }
    };

    checkIpLogin();
  }, [existingToken, existingUser, dispatch]);

  return null;
};

export default IPLoginHandler;
